import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  //   TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  User,
  Settings,
  Mail,
  Lock,
  Plus,
  Edit,
  Save,
  Users,
  AlertCircle,
  Pencil,
} from "lucide-react";

const BackendUrl = process.env.REACT_APP_Backend_Url;

// Couleurs personnalisées
const COLORS = {
  orange: "#E87E04",
  white: "#FFFFFF",
  green: "#008751",
  darkGreen: "#006B3F",
  lightOrange: "#FFE0B2",
  lightGreen: "#CCFFCC",
  gray: "#718096",
  lightGray: "#E2E8F0",
  paleGreen: "#E6FFEA",
};

function AdminSettings() {
  const navigate = useNavigate();
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [admins, setAdmins] = useState([]);

  // États pour le formulaire de profil
  const [profileForm, setProfileForm] = useState({
    name: "",
    email: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // États pour le formulaire de création d'admin
  const [createForm, setCreateForm] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);

  // Récupérer les informations de l'administrateur connecté
  useEffect(() => {
    const fetchAdminData = async () => {
      try {
        const adminData = JSON.parse(localStorage.getItem("AdminEcomme"));
        if (!adminData || !adminData.token) {
          navigate("/admin/login");
          return;
        }
        console.log(adminData);

        setCurrentAdmin(adminData);
        setProfileForm({
          name: adminData.name || "",
          email: adminData.email || "",
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });

        // Récupérer la liste des administrateurs
        await fetchAdmins(adminData.token);
      } catch (error) {
        console.error("Erreur lors de la récupération des données:", error);
        handleAlertError("Erreur de chargement des données");
      } finally {
        setLoading(false);
      }
    };

    fetchAdminData();
  }, [navigate]);

  // Récupérer la liste des administrateurs
  const fetchAdmins = async (token) => {
    try {
      const response = await axios.get(`${BackendUrl}/api/admin/all`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data && response.data.admins) {
        setAdmins(response.data.admins);
      }
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des administrateurs:",
        error
      );
    }
  };

  // Notifications
  const handleAlertSuccess = (message) => {
    toast.success(message, {
      position: toast.POSITION.TOP_RIGHT,
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const handleAlertError = (message) => {
    toast.error(message, {
      position: toast.POSITION.TOP_RIGHT,
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
    setError(message);
    setTimeout(() => setError(""), 3000);
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateChange = (e) => {
    const { name, value } = e.target;
    setCreateForm((prev) => ({ ...prev, [name]: value }));
  };

  // Mise à jour du profil administrateur
  const updateProfile = async () => {
    // Validation des données
    if (
      profileForm.newPassword &&
      profileForm.newPassword !== profileForm.confirmPassword
    ) {
      handleAlertError("Les mots de passe ne correspondent pas");
      return;
    }

    try {
      const updateData = {
        name: profileForm.name,
        email: profileForm.email,
      };

      if (profileForm.newPassword) {
        updateData.password = profileForm.newPassword;
      }

      const response = await axios.put(
        `${BackendUrl}/api/admin/${currentAdmin.id}`,
        updateData,
        {
          headers: {
            Authorization: `Bearer ${currentAdmin.token}`,
          },
        }
      );

      if (response.status === 200) {
        // Mettre à jour les données locales
        const updatedAdmin = {
          ...currentAdmin,
          name: response.data.admin.name,
          email: response.data.admin.email,
        };
        localStorage.setItem("AdminEcomme", JSON.stringify(updatedAdmin));
        setCurrentAdmin(updatedAdmin);

        // Réinitialiser les champs de mot de passe
        setProfileForm((prev) => ({
          ...prev,
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        }));

        handleAlertSuccess("Profil mis à jour avec succès");
      }
    } catch (error) {
      console.error("Erreur lors de la mise à jour du profil:", error);
      handleAlertError(
        error.response?.data?.message ||
          "Erreur lors de la mise à jour du profil"
      );
    }
  };

  // Création d'un nouvel administrateur
  const createAdmin = async () => {
    // Validation des données
    if (!createForm.name || !createForm.email || !createForm.password) {
      handleAlertError("Tous les champs sont obligatoires");
      return;
    }

    if (createForm.password !== createForm.confirmPassword) {
      handleAlertError("Les mots de passe ne correspondent pas");
      return;
    }

    try {
      const response = await axios.post(
        `${BackendUrl}/api/admin/register`,
        {
          name: createForm.name,
          email: createForm.email,
          password: createForm.password,
        },
        {
          headers: {
            Authorization: `Bearer ${currentAdmin.token}`,
          },
        }
      );

      if (response.status === 201) {
        // Réinitialiser le formulaire
        setCreateForm({
          name: "",
          email: "",
          password: "",
          confirmPassword: "",
        });

        // Rafraîchir la liste des administrateurs
        await fetchAdmins(currentAdmin.token);

        handleAlertSuccess("Administrateur créé avec succès");
      }
    } catch (error) {
      console.error("Erreur lors de la création de l'administrateur:", error);
      handleAlertError(
        error.response?.data?.message ||
          "Erreur lors de la création de l'administrateur"
      );
    }
  };

  // Éditer un administrateur existant
  const openEditDialog = (admin) => {
    setSelectedAdmin(admin);
    setDialogOpen(true);
  };

  const updateAdmin = async (e) => {
    e.preventDefault();

    if (!selectedAdmin) return;

    const updateData = {
      name: selectedAdmin.name,
      email: selectedAdmin.email,
    };

    if (
      selectedAdmin.newPassword &&
      selectedAdmin.newPassword === selectedAdmin.confirmPassword
    ) {
      updateData.password = selectedAdmin.newPassword;
    } else if (selectedAdmin.newPassword) {
      handleAlertError("Les mots de passe ne correspondent pas");
      return;
    }

    try {
      const response = await axios.put(
        `${BackendUrl}/api/admin/${selectedAdmin._id}`,
        updateData,
        {
          headers: {
            Authorization: `Bearer ${currentAdmin.token}`,
          },
        }
      );

      if (response.status === 200) {
        // Rafraîchir la liste des administrateurs
        await fetchAdmins(currentAdmin.token);
        setDialogOpen(false);
        handleAlertSuccess("Administrateur mis à jour avec succès");
      }
    } catch (error) {
      console.error(
        "Erreur lors de la mise à jour de l'administrateur:",
        error
      );
      handleAlertError(
        error.response?.data?.message ||
          "Erreur lors de la mise à jour de l'administrateur"
      );
    }
  };

  // Gestion des changements pour le formulaire d'édition
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setSelectedAdmin((prev) => ({ ...prev, [name]: value }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-green-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-green-800">Chargement en cours...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-green-50 p-6">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-green-800 mb-8 flex items-center">
          <Settings className="mr-2" /> Paramètres d'administration - Kassarmou
        </h1>

        {(error || successMessage) && (
          <Alert
            variant={error ? "destructive" : "default"}
            className={`mb-6 ${
              error
                ? "bg-red-50 border-red-200 text-red-800"
                : "bg-green-50 border-green-200 text-green-800"
            }`}
          >
            <AlertCircle className="h-4 w-4 mr-2" />
            <AlertDescription>{error || successMessage}</AlertDescription>
          </Alert>
        )}

        <Tabs defaultValue="profile" className="w-full">
          <TabsList className="mb-6 bg-green-100 p-1 rounded-lg">
            <TabsTrigger
              value="profile"
              className="data-[state=active]:bg-white data-[state=active]:text-green-800"
            >
              <User className="mr-2 h-4 w-4" /> Mon Profil
            </TabsTrigger>
            <TabsTrigger
              value="admins"
              className="data-[state=active]:bg-white data-[state=active]:text-green-800"
            >
              <Users className="mr-2 h-4 w-4" /> Gestion des Administrateurs
            </TabsTrigger>
          </TabsList>

          {/* Onglet Profil */}
          <TabsContent value="profile">
            <Card className="shadow-md border border-green-100">
              <CardHeader className="bg-gradient-to-r from-green-50 to-green-100 border-b border-green-200">
                <CardTitle className="text-xl text-green-800">
                  <User className="inline mr-2" size={20} /> Informations de
                  profil
                </CardTitle>
                <CardDescription>
                  Modifiez vos informations personnelles et votre mot de passe
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6 space-y-6">
                <div className="space-y-2">
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Nom complet
                  </label>
                  <div className="flex items-center relative">
                    <User className="absolute left-3 text-gray-500" size={18} />
                    <Input
                      id="name"
                      name="name"
                      value={profileForm.name}
                      onChange={handleProfileChange}
                      className="pl-10 bg-white border border-gray-200 focus:border-green-500 focus:ring focus:ring-green-200"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Adresse email
                  </label>
                  <div className="flex items-center relative">
                    <Mail className="absolute left-3 text-gray-500" size={18} />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={profileForm.email}
                      onChange={handleProfileChange}
                      className="pl-10 bg-white border border-gray-200 focus:border-green-500 focus:ring focus:ring-green-200"
                    />
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <h3 className="text-lg font-medium text-gray-800 mb-4">
                    Changer le mot de passe
                  </h3>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label
                        htmlFor="currentPassword"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Mot de passe actuel
                      </label>
                      <div className="flex items-center relative">
                        <Lock
                          className="absolute left-3 text-gray-500"
                          size={18}
                        />
                        <Input
                          id="currentPassword"
                          name="currentPassword"
                          type="password"
                          value={profileForm.currentPassword}
                          onChange={handleProfileChange}
                          className="pl-10 bg-white border border-gray-200 focus:border-green-500 focus:ring focus:ring-green-200"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label
                        htmlFor="newPassword"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Nouveau mot de passe
                      </label>
                      <div className="flex items-center relative">
                        <Lock
                          className="absolute left-3 text-gray-500"
                          size={18}
                        />
                        <Input
                          id="newPassword"
                          name="newPassword"
                          type="password"
                          value={profileForm.newPassword}
                          onChange={handleProfileChange}
                          className="pl-10 bg-white border border-gray-200 focus:border-green-500 focus:ring focus:ring-green-200"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Le mot de passe doit contenir au moins 8 caractères, une
                        majuscule, une minuscule, un chiffre et un caractère
                        spécial.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <label
                        htmlFor="confirmPassword"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Confirmer le nouveau mot de passe
                      </label>
                      <div className="flex items-center relative">
                        <Lock
                          className="absolute left-3 text-gray-500"
                          size={18}
                        />
                        <Input
                          id="confirmPassword"
                          name="confirmPassword"
                          type="password"
                          value={profileForm.confirmPassword}
                          onChange={handleProfileChange}
                          className="pl-10 bg-white border border-gray-200 focus:border-green-500 focus:ring focus:ring-green-200"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="bg-green-50 border-t border-green-100 flex justify-end">
                <Button
                  onClick={updateProfile}
                  className="bg-green-600 hover:bg-green-700 text-white transition-all"
                  style={{ backgroundColor: COLORS.green }}
                >
                  <Save className="mr-2 h-4 w-4" /> Enregistrer les
                  modifications
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>

          {/* Onglet Gestion des Administrateurs */}
          <TabsContent value="admins">
            <div className="grid gap-6 md:grid-cols-12">
              {/* Liste des administrateurs */}
              <div className="md:col-span-8">
                <Card className="shadow-md border border-green-100">
                  <CardHeader className="bg-gradient-to-r from-green-50 to-green-100 border-b border-green-200">
                    <CardTitle className="text-xl text-green-800">
                      <Users className="inline mr-2" size={20} /> Liste des
                      administrateurs
                    </CardTitle>
                    <CardDescription>
                      Gérez les comptes administrateurs de la plateforme
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <div className="text-center text-sm text-gray-500 mt-4">
                        Liste des administrateurs du système
                      </div>
                      <TableHeader className="bg-green-50">
                        <TableRow>
                          <TableHead>Nom</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Date de création</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {admins?.length > 0 ? (
                          admins?.map((admin) => (
                            <TableRow key={admin._id}>
                              <TableCell className="font-medium">
                                {admin.name}
                              </TableCell>
                              <TableCell>{admin.email}</TableCell>
                              <TableCell>
                                {new Date(
                                  admin.dateCreating
                                ).toLocaleDateString()}
                              </TableCell>
                              <TableCell className="text-right">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openEditDialog(admin)}
                                  className="text-green-700 border-green-200 hover:bg-green-50"
                                >
                                  <Pencil className="h-4 w-4 mr-1" /> Éditer
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell
                              colSpan={4}
                              className="text-center py-8 text-gray-500"
                            >
                              Aucun administrateur trouvé
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </div>

              {/* Formulaire de création d'admin */}
              <div className="md:col-span-4">
                <Card className="shadow-md border border-green-100">
                  <CardHeader className="bg-gradient-to-r from-green-50 to-green-100 border-b border-green-200">
                    <CardTitle className="text-xl text-green-800">
                      <Plus className="inline mr-2" size={20} /> Nouvel
                      administrateur
                    </CardTitle>
                    <CardDescription>
                      Créez un nouveau compte administrateur
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-6 space-y-4">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Nom complet
                      </label>
                      <Input
                        name="name"
                        value={createForm.name}
                        onChange={handleCreateChange}
                        className="bg-white border border-gray-200 focus:border-green-500 focus:ring focus:ring-green-200"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Adresse email
                      </label>
                      <Input
                        name="email"
                        type="email"
                        value={createForm.email}
                        onChange={handleCreateChange}
                        className="bg-white border border-gray-200 focus:border-green-500 focus:ring focus:ring-green-200"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Mot de passe
                      </label>
                      <Input
                        name="password"
                        type="password"
                        value={createForm.password}
                        onChange={handleCreateChange}
                        className="bg-white border border-gray-200 focus:border-green-500 focus:ring focus:ring-green-200"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Minimum 8 caractères avec majuscules, minuscules,
                        chiffres et caractères spéciaux
                      </p>
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Confirmer le mot de passe
                      </label>
                      <Input
                        name="confirmPassword"
                        type="password"
                        value={createForm.confirmPassword}
                        onChange={handleCreateChange}
                        className="bg-white border border-gray-200 focus:border-green-500 focus:ring focus:ring-green-200"
                      />
                    </div>
                  </CardContent>
                  <CardFooter className="bg-green-50 border-t border-green-100">
                    <Button
                      onClick={createAdmin}
                      className="w-full bg-green-600 hover:bg-green-700 text-white transition-all"
                      style={{ backgroundColor: COLORS.green }}
                    >
                      <Plus className="mr-2 h-4 w-4" /> Créer l'administrateur
                    </Button>
                  </CardFooter>
                </Card>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Dialogue d'édition d'administrateur */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Modifier l'administrateur</DialogTitle>
            <DialogDescription>
              Modifiez les informations de l'administrateur sélectionné
            </DialogDescription>
          </DialogHeader>

          {selectedAdmin && (
            <form onSubmit={updateAdmin} className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Nom complet
                </label>
                <Input
                  name="name"
                  value={selectedAdmin.name || ""}
                  onChange={handleEditChange}
                  className="bg-white border border-gray-200 focus:border-green-500 focus:ring focus:ring-green-200"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Adresse email
                </label>
                <Input
                  name="email"
                  type="email"
                  value={selectedAdmin.email || ""}
                  onChange={handleEditChange}
                  className="bg-white border border-gray-200 focus:border-green-500 focus:ring focus:ring-green-200"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Nouveau mot de passe (laisser vide pour ne pas changer)
                </label>
                <Input
                  name="newPassword"
                  type="password"
                  value={selectedAdmin.newPassword || ""}
                  onChange={handleEditChange}
                  className="bg-white border border-gray-200 focus:border-green-500 focus:ring focus:ring-green-200"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Confirmer le nouveau mot de passe
                </label>
                <Input
                  name="confirmPassword"
                  type="password"
                  value={selectedAdmin.confirmPassword || ""}
                  onChange={handleEditChange}
                  className="bg-white border border-gray-200 focus:border-green-500 focus:ring focus:ring-green-200"
                />
              </div>

              <DialogFooter className="pt-4">
                <Button
                  type="submit"
                  className="bg-green-600 hover:bg-green-700 text-white transition-all"
                  style={{ backgroundColor: COLORS.green }}
                >
                  <Save className="mr-2 h-4 w-4" /> Enregistrer
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <ToastContainer />
    </div>
  );
}

export default AdminSettings;
